import { Sidebar } from "src/components/Sidebar/Sidebar"
import { ProjectImage } from "src/components/ProjectImage/ProjectImage"
import "./styles.scss"

const MainContent = () => {
  return (
    <div className="main-content p-4">
      <div className="header mb-4">
        <h1 className="title">Pacer Financing Tools</h1>
        <h2 className="subtitle">Full Stack Development of Consumer Financing Tools</h2>
      </div>

      <div className="description mb-5">
        <p>
        While at Pacer, we were approached by a partner in the dealership website space who needed a way to embed financing application forms into their clients' sites. We recognized the opportunity to build a valuable tool for both large and small dealerships that currently lacked direct-to-site financing capabilities. I led the project from concept to MVP, developing a fully embeddable form system, a secure backend management portal for dealership users, and implementing advanced security and compliance features to meet industry standards.
        </p>
      </div>

      <div className="project-screenshot">
        <ProjectImage 
          src="/assets/Screenshots/financingForm.png" 
          alt="Pacer Financing Form"
          description="Embedded financing application form"
        />
      </div>

      <div className="description mb-5">
        <p>
          This project pushed me out of my comfort zone and deep into the world of security and compliance. I gained significant experience with SOC2 standards, encryption at rest and in transit using Google Key Management, and implemented best practices including CSRF protection and Subresource Integrity (SRI).
        </p>
      </div>

      <div className="description things-i-worked-on mb-5">
        <h4>Things I Worked On</h4>
        <p>Highlights of the features I developed include:</p>
        <ul>
          <li><a href="#financing-form" className="work-link">Embeddable Financing Form (built with ESBuild)</a></li>
          <li><a href="#front-end" className="work-link"> Management Dashboard for Dealership Users</a></li>
          <li><a href="#audit-trail" className="work-link">Audit Trail Middleware & Dashboard</a></li>
          <li><a href="#user-management" className="work-link">User and Permissions Management System</a></li>
        </ul>
      </div>

      <div id="financing-form" className="description mb-5">
        <h4>Embeddable Financing Form</h4>
        <p>
        I built a modular, embeddable financing form using ESBuild and served it via CDN. The form is customizable for various dealership needs and includes robust validation and security layers. Each dealership receives a unique asymmetric key via Google Key Management. To protect form integrity and user data, I implemented both SRI and CSRF protections.
        </p>
        <p>
        I also worked on spam prevention measures, including a captcha and a single entry per user system. The form is designed to be embeddable on any website, and I built a simple script to allow dealerships to easily add the form to their site. The form is also designed to be mobile friendly.
        </p>
        
      </div>

      <div id="front-end" className="description mb-5">
        <h4>Front-End for Dealership users</h4>
        <p>
        I developed a secure dashboard in TypeScript and React, allowing dealerships to manage financing applications and user roles. The dashboard interacts with a Google Cloud Platform backend via API and supports multi-dealership environments. A flexible permissions system allows dealerships to define access rules—for example, some allow sales staff to view customer details, others don&apos;t.
        </p>
        <ProjectImage 
          src="/assets/Screenshots/financingApplicationDashboard.png" 
          alt="Pacer Financing Form"
          description="Dashboard to see inbound applications"
          width="110%"
        />

        <ProjectImage 
          src="/assets/Screenshots/financingApplicationDetail.png" 
          alt="Pacer Financing Form"
          description="Click to see details, manage, and run credit checks"
          width="100%"
        />
      </div>

      <div id="audit-trail" className="description mb-5">
        <h4>Audit Trail</h4>
        <p>
        To meet SOC2 compliance, I implemented backend middleware to log all dealership user actions. These logs are stored in a database and visualized through a dedicated dashboard that also supports report generation for audits and internal review.
        </p>
        <ProjectImage 
          src="/assets/Screenshots/financingAuditLogs.png" 
          alt="Financing Form"
          description="Audit trail dashboard for dealership Admin users"
          width="100%"
        />
      </div>

      <div id="user-management" className="description mb-5">
        <h4>User and Permissions Management</h4>
        <p>
        Dealership admins needed tools to manage their teams and enforce access controls. I built a user management interface backed by middleware that ensures all permission checks are enforced server-side. Unauthorized access attempts are logged and flagged via the audit trail to alert administrators in real time.
        </p>
        <ProjectImage 
          src="/assets/Screenshots/financingUserPermissions.png" 
          alt="Analytics Dashboard"
          description="Sample user permissions table"
          width="90%"
        />
      </div>

      <div id="conclusion" className="description mb-5">
        <h4>Conclusion</h4>
        <p>
        This project was an invaluable learning experience in building secure, multi-tenant applications. I deepened my expertise in React, TypeScript, middleware, and cloud-based backend systems, while also navigating the nuances of SOC2 compliance and secure software design.
        </p>
        <p>
        We're currently in early-stage integration with our first dealership partner. Next steps include integration with dealer DMS systems, connecting to digital credit check providers, and working toward full SOC2 certification.
        </p>
      </div>
    </div>
  )
}

const ProjectLayout: React.FC<{
  sidebar: React.ReactNode;
  children: React.ReactNode;
}> = ({ sidebar, children }) => (
  <div className="container-fluid p-0">
    <div className="row g-0">
      <div className="col-md-3">{sidebar}</div>
      <div className="col-md-9">{children}</div>
    </div>
  </div>
)

export default function PacerFinancingProject() {
  const sidebarProps = {
    toolUrl: "",
    disabledLink: true,
    responsibilities: [
      "ESBuild Embeddable Financing Form",
      "Typescript React Frontend Dashboard",
      "Express.JS Backend",
      "Audit Trail Middleware & Dashboard",
      "User Management Middleware & Dashboard",
      "Google Key Management Integration",
      "Advanced form Security",
      "SOC2 Compliance",
    ]
  }

  return (
    <ProjectLayout sidebar={<Sidebar {...sidebarProps} />}>
      <MainContent />
    </ProjectLayout>
  )
}
